import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../layout";
import Header from "../header/Header";
import CustomButton from "../shared/common/customButton";
import PDFDownloadButton from "../shared/pdf/pdfDownloadBtn";
import CountrySelectInput from "../shared/countrySelectInput";
import axios from "axios";
import { api, API_BASE_URL, API_DEPLOYED_URL } from "../../config/api";
import { FaArrowLeft, FaCirclePlus, FaCircleMinus } from "react-icons/fa6";
import { Radio, Select } from 'antd';
import ServiceTemp from "./servicesTemp/temp";
import { toast } from "react-toastify";
import { useMyContext } from "../../context/MyContext";
import { generateIOR } from "../shared/pdf/generateIOR";
import { db } from "../../config/firebase";
import { addDoc, collection, doc, updateDoc, getDocs, getDoc, setDoc, query as fsQuery, where } from "firebase/firestore";
import { generateTrackingNumber } from "../../lib/helper/trackingNumber";
import { AmountFormat } from "../../lib/helper/amountFormat";
import InsuranceCheckbox from "../forms/helperComponent/insuranceCheckBox";
import DiscountInput from "./helperComponent/DiscountInput";

const { Option } = Select;

const deepEqual = (obj1, obj2, keysToCompare) => {
    // If both objects are null/undefined or are the same reference, return true
    if (obj1 === obj2) {
        return true;
    }
    // Special case for handling numbers vs strings - this is critical for our comparison
    if ((typeof obj1 === 'number' && typeof obj2 === 'string') ||
        (typeof obj1 === 'string' && typeof obj2 === 'number')) {
        const num1 = Number(obj1);
        const num2 = Number(obj2);
        if (!isNaN(num1) && !isNaN(num2)) {
            const result = num1 === num2;
            return result;
        }
    }
    // If either is not an object or is null after numeric check
    if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 == null || obj2 == null) {
        // Handle boolean vs string comparison
        if ((typeof obj1 === 'boolean' && typeof obj2 === 'string') ||
            (typeof obj1 === 'string' && typeof obj2 === 'boolean')) {
            const bool1 = typeof obj1 === 'string' ? obj1.toLowerCase() === 'true' : obj1;
            const bool2 = typeof obj2 === 'string' ? obj2.toLowerCase() === 'true' : obj2;
            const result = bool1 === bool2;
            return result;
        }
        const result = String(obj1).trim() === String(obj2).trim();
        return result;
    }
    // Handle arrays specifically before checking keys
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        // Special handling for services array - THIS IS THE CRITICAL FIX  If we're comparing services arrays, we need to sort and compare differently
        if (keysToCompare && keysToCompare.includes('services')) {
            // For services array, we need to compare in a way that allows for different object references but same content
            if (obj1.length !== obj2.length) {
                return false;
            }
            // For services comparison, convert both arrays to strings after sorting by description for comparison
            const sortAndStringify = (arr) => {
                return JSON.stringify(
                    [...arr].sort((a, b) => {
                        if (a.description !== b.description) {
                            return a.description.localeCompare(b.description);
                        }
                        return Number(a.exclVAT) - Number(b.exclVAT);
                    })
                );
            };
            const str1 = sortAndStringify(obj1);
            const str2 = sortAndStringify(obj2);
            const result = str1 === str2;
            return result;
        }
        // Standard array comparison (for non-services arrays)
        if (obj1.length !== obj2.length) {
            return false;
        }
        for (let i = 0; i < obj1.length; i++) {
            if ((typeof obj1[i] === 'number' && typeof obj2[i] === 'string') ||
                (typeof obj1[i] === 'string' && typeof obj2[i] === 'number')) {
                const num1 = Number(obj1[i]);
                const num2 = Number(obj2[i]);
                if (!isNaN(num1) && !isNaN(num2)) {
                    const result = num1 === num2;
                    if (result) {
                        continue;
                    }
                }
            }
            if (obj1[i] === obj2[i]) {
                continue;
            }
            if (typeof obj1[i] === 'object' && obj1[i] !== null &&
                typeof obj2[i] === 'object' && obj2[i] !== null) {
                const allKeys = [...new Set([
                    ...(Object.keys(obj1[i]) || []),
                    ...(Object.keys(obj2[i]) || [])
                ])];
                if (!deepEqual(obj1[i], obj2[i], allKeys)) {
                    return false;
                }
                continue;
            }
            const str1 = String(obj1[i]).trim();
            const str2 = String(obj2[i]).trim();
            if (str1 === str2) {
                continue;
            }
            return false;
        }
        return true;
    }
    // If we're comparing objects and not arrays.  Only compare specified keys if keysToCompare is provided, otherwise compare all keys
    const keysToCheck = keysToCompare || [...new Set([...Object.keys(obj1), ...Object.keys(obj2)])];
    for (let key of keysToCheck) {
        // console.log(`Checking key: "${key}"`);
        // Special handling for services array
        if (key === 'services') {
            const servicesEqual = deepEqual(obj1[key], obj2[key], ['services']);
            if (!servicesEqual) {
                return false;
            }
            continue;
        }
        // Check if both objects have the key
        const obj1HasKey = key in obj1;
        const obj2HasKey = key in obj2;
        // If one has the key but the other doesn't, return false
        if (obj1HasKey !== obj2HasKey) {
            return false;
        }
        // If neither has the key, continue to next key
        if (!obj1HasKey && !obj2HasKey) {
            continue;
        }
        const val1 = obj1[key];
        const val2 = obj2[key];
        // Check if values are strictly equal
        if (val1 === val2) {
            continue;
        }
        // For arrays, use recursive comparison
        if (Array.isArray(val1) && Array.isArray(val2)) {
            if (!deepEqual(val1, val2)) {
                return false;
            }
            continue;
        }
        if (typeof val1 === 'object' && val1 !== null &&
            typeof val2 === 'object' && val2 !== null) {
            const nestedKeys = [...new Set([...Object.keys(val1), ...Object.keys(val2)])];
            if (!deepEqual(val1, val2, nestedKeys)) {
                return false;
            }
            continue;
        }
        // IMPORTANT: Special handling for the specific issue with numeric values and strings
        if ((typeof val1 === 'number' && typeof val2 === 'string') ||
            (typeof val1 === 'string' && typeof val2 === 'number')) {
            const num1 = Number(val1);
            const num2 = Number(val2);
            if (!isNaN(num1) && !isNaN(num2)) {
                const result = num1 === num2;
                if (result) {
                    continue;
                }
            }
        }
        // Handle boolean vs string comparison
        if ((typeof val1 === 'boolean' && typeof val2 === 'string') ||
            (typeof val1 === 'string' && typeof val2 === 'boolean')) {
            const bool1 = typeof val1 === 'string' ? val1.toLowerCase() === 'true' : val1;
            const bool2 = typeof val2 === 'string' ? val2.toLowerCase() === 'true' : val2;
            const result = bool1 === bool2;
            if (result) {
                continue;
            }
        }
        const str1 = String(val1).trim();
        const str2 = String(val2).trim();
        const result = str1 === str2;
        if (result) {
            continue;
        }
        return false;
    }
    return true;
};

const CompleteEstimationForm = () => {
    const { user } = useMyContext();
    const { estimateId } = useParams();
    const navigate = useNavigate();
    const [isSendLoading, setIsSendLoading] = useState(false);
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [templateSelected, setTemplateSelected] = useState(null);
    const [initialValues, setInitialValues] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [totalItemsValue, setTotalItemsValue] = useState(0);

    const [eccnCodes, setEccnCodes] = useState([]);
    const [selectedEccn, setSelectedEccn] = useState(null);
    const [discountType, setDiscountType] = useState("percentage");
    const [discountValue, setDiscountValue] = useState("");

    const [values, setValues] = useState({
        userId: user?.uid || "",
        userName: user?.name || "",
        userCity: user?.city || "",
        userCountry: user?.country || "",
        postalCode: user?.postalCode || "",
        email: "",
        status: false,
        requiredDoc: "not_Required",
        dimensions: [
            {
                weight: { value: "", unit: "kg" },
                length: { value: "", unit: "cm" },
                width: { value: "", unit: "cm" },
                height: { value: "", unit: "cm" },
                quantity: { value: "" },
            },
        ],
        insuranceSelected: "NO",
        airfreightCurrency: "USD",
        afcWeightUnit: "kg",
        selectedService: "IOR",
        selectedTemplate: "",
        itemsCurrency: "USD",
        items: [
            {
                itemName: "",
                quantity: "",
                itemValue: "",
                countryOrigin: "",
                itemHSCode: "",
            },
        ],
        loadName: "",
        loadCompanyName: "",
        loadReferenceCustomer: "",
        loadAddress: "",
        loadZipCode: "",
        loadCity: "",
        loadCountry: "",
        loadMobileNumber: "",
        unloadName: "",
        unloadCompanyName: "",
        unloadReferenceCustomer: "",
        unloadAddress: "",
        unloadZipCode: "",
        unloadCity: "",
        unloadCountry: "",
        unloadMobileNumber: "",
        pkgType: "pallet",
        services: [],
        currency: "USD",
        vatPercentage: "0",
        subTotal: "0.00",
        vatAmount: "0.00",
        total: "0.00",
        discount: null,
        insurance: "0.00",
        isInsuranceChecked: false,
        cifValue: { value: "0", currency: "USD" },
        eccnCode: null, // New field for ECCN
    });


    useEffect(() => {
        const fetchEccnCodes = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/eccn`);
                setEccnCodes(response.data);
            } catch (error) {
                console.error("Error fetching ECCN codes:", error);
                toast.error("Failed to load ECCN codes");
            }
        };
        fetchEccnCodes();
    }, []);

    useEffect(() => {
        if (selectedEccn) {
            setValues((prev) => {
                const newValues = { ...prev, eccnCode: selectedEccn };
                updateHasChanges(newValues);
                return newValues;
            });
        }
    }, [selectedEccn]);

    const fetchClientEstimateSummary = async (estimateId) => {
        try {
            const response = await api.get(`${API_BASE_URL}/clientestimates/summary/${estimateId}`);
            return response.data.estimateSummary;
        } catch (error) {
            console.error("Error fetching client estimate summary:", error);
            toast.error("Error fetching estimate summary");
            return null;
        }
    };

    const updateHasChanges = (newValues) => {
        if (initialValues) {
            const keysToCompare = [
                "userId",
                "userName",
                "userCity",
                "userCountry",
                "postalCode",
                "email",
                "status",
                "requiredDoc",
                "dimensions",
                "insuranceSelected",
                "airfreightCurrency",
                "afcWeightUnit",
                "selectedService",
                "selectedTemplate",
                "itemsCurrency",
                "items",
                "loadName",
                "loadCompanyName",
                "loadReferenceCustomer",
                "loadAddress",
                "loadZipCode",
                "loadCity",
                "loadCountry",
                "loadMobileNumber",
                "unloadName",
                "unloadCompanyName",
                "unloadReferenceCustomer",
                "unloadAddress",
                "unloadZipCode",
                "unloadCity",
                "unloadCountry",
                "unloadMobileNumber",
                "pkgType",
                "services",
                "currency"
            ];
            // console.log("Initial services:", JSON.stringify(initialValues.services));
            // console.log("New services:", JSON.stringify(newValues.services));
            const changesDetected = !deepEqual(newValues, initialValues, keysToCompare);
            // console.log("chnages detected" ,changesDetected )
            setHasChanges(changesDetected);
        }
    };

    const fetchEstimate = async () => {
        try {
            const response = await api.get(`${API_BASE_URL}/clientestimates/id/${estimateId}`);
            const estimateData = response.data;

            const initialDimensions =
                estimateData.dimensions && estimateData.dimensions.length > 0
                    ? estimateData.dimensions.map((dim) => ({
                        quantity: { value: dim.quantity?.value || dim.quantity || "" },
                        weight: { value: dim.weight?.value || "", unit: dim.weight?.unit || "kg" },
                        length: { value: dim.length?.value || "", unit: dim.length?.unit || "cm" },
                        width: { value: dim.width?.value || "", unit: dim.width?.unit || "cm" },
                        height: { value: dim.height?.value || "", unit: dim.height?.unit || "cm" },
                    }))
                    : values.dimensions;

            const initialItems =
                estimateData.items && estimateData.items.length > 0
                    ? estimateData.items.map((item) => ({
                        itemName: item.itemName || "",
                        quantity: item.quantity || 1,
                        itemValue: item.itemValue || "",
                        countryOrigin: item.countryOrigin || "",
                        itemHSCode: item.itemHSCode || "",
                    }))
                    : values.items;

            const serviceItems =
                estimateData.estimateSummary?.services?.map((service) => ({
                    numberOfItem: "1",
                    description: service.description,
                    exclVAT: service.value.toString(),
                })) || [];

            const dhlTaxItems =
                estimateData.estimateSummary?.dhlTaxes?.map((tax) => ({
                    numberOfItem: "1",
                    description: tax.name,
                    exclVAT: tax.value.toString(),
                })) || [];

            const combinedServices = [...serviceItems, ...dhlTaxItems];

            const fetchedValues = {
                ...values,
                userId: estimateData.userId || values.userId,
                userName: estimateData.userName || values.userName,
                userCity: estimateData.userCity || values.userCity,
                userCountry: estimateData.userCountry || values.userCountry,
                postalCode: estimateData.postalCode || values.postalCode,
                email: estimateData.email || values.email,
                status: estimateData.status || false,
                requiredDoc: estimateData.requiredDoc || "not_Required",
                dimensions: initialDimensions,
                insuranceSelected: estimateData.insuranceSelected || "NO",
                airfreightCurrency: estimateData.airfreightCurrency || "USD",
                afcWeightUnit: estimateData.afcWeightUnit || "kg",
                selectedService: estimateData.selectedService || "IOR",
                selectedTemplate: estimateData.selectedService || "",
                itemsCurrency: estimateData.itemsCurrency || "USD",
                items: initialItems,
                loadName: estimateData.userName || values.loadName,
                loadCompanyName: estimateData.loadAddress_CompanyName || values.loadCompanyName,
                loadReferenceCustomer: estimateData.loadAddress_ReferenceCustomer || values.loadReferenceCustomer,
                loadAddress: estimateData.loadAddress || values.loadAddress,
                loadZipCode: estimateData.postalCode || values.loadZipCode,
                loadCity: estimateData.originCity || values.loadCity,
                loadCountry: estimateData.loadCountry || values.loadCountry,
                loadMobileNumber: estimateData.loadAddress_PhoneNumber || values.loadMobileNumber,
                unloadName: estimateData.unloadName || values.unloadName,
                unloadCompanyName: estimateData.unloadAddress_CompanyName || values.unloadCompanyName,
                unloadReferenceCustomer: estimateData.unloadAddress_ReferenceCustomer || values.unloadReferenceCustomer,
                unloadAddress: estimateData.unloadAddress || values.unloadAddress,
                unloadZipCode: estimateData.unloadPostalCode || values.unloadZipCode,
                unloadCity: estimateData.destinationCity || values.unloadCity,
                unloadCountry: estimateData.unloadCountry || values.unloadCountry,
                unloadMobileNumber: estimateData.unloadAddress_PhoneNumber || values.unloadMobileNumber,
                pkgType: estimateData.pkgType || "pallet",
                services: combinedServices,
                currency: estimateData.estimateSummary?.currency || "USD",
                vatPercentage: (estimateData.vatPercentage ?? "") === "" ? "0" : String(estimateData.vatPercentage),
                subTotal: estimateData.subTotal || "0.00",
                vatAmount: estimateData.vatAmount || "0.00",
                total: estimateData.total || "0.00",
                discount: estimateData.discount || estimateData.tradeOSData?.discount || null,
                eccnCode: estimateData.eccnCode || null,
                approvalStatus: estimateData.approvalStatus || "Draft",
            };

            // Try to hydrate VAT and Discount from latest quote in Firestore if present for this request
            try {
                const quotesRef = collection(db, "quotes");
                const q = fsQuery(quotesRef, where("requestId", "==", estimateId));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    // pick the newest by quoteCreatedDate (ISO string) or creationDate if present
                    const docs = snap.docs
                        .map(d => d.data())
                        .sort((a, b) => (new Date(b.quoteCreatedDate || b.creationDate || 0)) - (new Date(a.quoteCreatedDate || a.creationDate || 0)));
                    const latest = docs[0];
                    const vatFromQuote = latest?.vatPercentage;
                    if (vatFromQuote !== undefined && vatFromQuote !== null && String(vatFromQuote) !== "") {
                        fetchedValues.vatPercentage = String(vatFromQuote);
                    }
                    if (latest?.discount) {
                        fetchedValues.discount = latest.discount;
                    }
                }
            } catch (err) {
                console.warn("VAT/Discount fetch from Firestore quotes failed (non-blocking):", err);
            }

            const existingDiscount = fetchedValues.discount || estimateData.discount || estimateData.tradeOSData?.discount || estimateData.estimateSummary?.discount;
            if (existingDiscount) {
                setDiscountType(existingDiscount.type || "percentage");
                setDiscountValue(existingDiscount.value !== undefined ? existingDiscount.value : "");
            }

            setValues(fetchedValues);
            setInitialValues(JSON.parse(JSON.stringify(fetchedValues)));
            setTemplateSelected(estimateData.selectedService ? true : null);
            setSelectedEccn(estimateData.eccnCode || null);
        } catch (error) {
            console.error("Error fetching estimate:", error);
            toast.error("Failed to load estimate data");
        }
    };

    useEffect(() => {
        if (estimateId) fetchEstimate();
    }, [estimateId]);

    useEffect(() => {
        const calculateTotals = () => {
            let subTotal = 0;
            let discountableSubTotal = 0;

            (values.services || []).forEach((service) => {
                const totalForService =
                    parseFloat(service.numberOfItem || 1) * parseFloat(service.exclVAT || 0);
                subTotal += totalForService;

                const desc = (service.description || "").trim().toLowerCase();
                const isTaxOrDuty =
                    desc === "vat" ||
                    desc.includes("duty") ||
                    desc.includes("duties") ||
                    desc.includes("tax");

                if (!isTaxOrDuty) {
                    discountableSubTotal += totalForService;
                }
            });

            // Calculate Discount — applies only to base services subtotal, excluding VAT/Duties/Taxes
            const rawDiscount = parseFloat(discountValue) || 0;
            let discountAmount = 0;
            if (rawDiscount > 0) {
                if (discountType === "percentage") {
                    discountAmount = (discountableSubTotal * rawDiscount) / 100;
                } else {
                    discountAmount = rawDiscount;
                }
                discountAmount = Math.min(discountAmount, discountableSubTotal);
            }
            const discountedSubTotal = subTotal - discountAmount;

            const vatPercentage = parseFloat(values.vatPercentage || 0);
            const vatAmount = vatPercentage === 0 ? 0 : (subTotal * vatPercentage) / 100;

            let total = discountedSubTotal + vatAmount;

            if (values.insuranceSelected === "YES") {
                const insuranceValue = parseFloat(values.insurance || 0);
                total += insuranceValue;
            }

            const discountData = discountAmount > 0
                ? {
                    type: discountType,
                    value: rawDiscount,
                    amount: parseFloat(discountAmount.toFixed(2)),
                    servicesSubtotalBeforeDiscount: discountableSubTotal,
                }
                : null;

            setValues(prev => ({
                ...prev,
                subTotal: subTotal.toFixed(2),
                vatAmount: vatAmount.toFixed(2),
                total: total.toFixed(2),
                discount: discountData,
            }));
        };

        calculateTotals();
    }, [values.services, values.vatPercentage, values.insuranceSelected, values.insurance, discountType, discountValue]);


    const handleChange = (event) => {
        const { name, value } = event.target;
        setValues(prev => {
            const newValues = { ...prev, [name]: value };
            updateHasChanges(newValues);
            return newValues;
        });
        if (attemptedSubmit) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleDimensionChange = (index, field, e) => {
        const { value } = e.target;
        setValues(prev => {
            const newDimensions = [...prev.dimensions];
            newDimensions[index][field] = { ...newDimensions[index][field], value };
            const newValues = { ...prev, dimensions: newDimensions };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleDimensionUnitChange = (index, field, unit) => {
        setValues(prev => {
            const newDimensions = [...prev.dimensions];
            newDimensions[index][field] = { ...newDimensions[index][field], unit };
            const newValues = { ...prev, dimensions: newDimensions };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleAddDimension = () => {
        setValues(prev => {
            const newValues = {
                ...prev,
                dimensions: [...prev.dimensions, {
                    weight: { value: "", unit: "kg" },
                    length: { value: "", unit: "cm" },
                    width: { value: "", unit: "cm" },
                    height: { value: "", unit: "cm" },
                    quantity: { value: "" }
                }]
            };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleRemoveDimension = (index) => {
        if (values.dimensions.length > 1) {
            setValues(prev => {
                const newValues = {
                    ...prev,
                    dimensions: prev.dimensions.filter((_, i) => i !== index)
                };
                updateHasChanges(newValues);
                return newValues;
            });
        }
    };

    const handleItemChange = (index, field, value) => {
        setValues(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index][field] = value;
            const newValues = { ...prev, items: updatedItems };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleAddItem = () => {
        setValues(prev => {
            const newValues = {
                ...prev,
                items: [...prev.items, {
                    itemName: "",
                    quantity: 1,
                    itemValue: "",
                    countryOrigin: "",
                    itemHSCode: ""
                }]
            };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleRemoveItem = (index) => {
        if (values.items.length > 1) {
            setValues(prev => {
                const newValues = {
                    ...prev,
                    items: prev.items.filter((_, i) => i !== index)
                };
                updateHasChanges(newValues);
                return newValues;
            });
        }
    };

    const handleSelectChange = (name, value) => {
        setValues(prev => {
            const newValues = { ...prev, [name]: value };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleInsuranceChange = (e) => {
        setValues(prev => {
            const newValues = { ...prev, insuranceSelected: e.target.value };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const handleRequiredDocChange = (e) => {
        setValues(prev => {
            const newValues = { ...prev, requiredDoc: e.target.value };
            updateHasChanges(newValues);
            return newValues;
        });
    };

    const validateForm = () => {
        let errors = {};
        if (!values.loadCountry) errors.loadCountry = true;
        if (!values.unloadCountry) errors.unloadCountry = true;
        if (!values.loadName) errors.loadName = true;
        if (!values.unloadName) errors.unloadName = true;
        // VAT percentage is optional; only flag if present and invalid (negative)
        if (values.vatPercentage !== "" && parseFloat(values.vatPercentage) < 0) errors.vatPercentage = true;
        values.dimensions.forEach((dim, index) => {
            ["weight", "length", "width", "height", "quantity"].forEach((field) => {
                if (!dim[field].value) errors[`${field}-${index}`] = true;
            });
        });
        values.items.forEach((item, index) => {
            if (!item.itemName) errors[`itemName-${index}`] = true;
            if (!item.itemValue) errors[`itemValue-${index}`] = true;
            if (!item.quantity) errors[`quantity-${index}`] = true;
        });
        // Add ECCN validation when EOR is selected
        if (values.selectedService === "EOR" && !values.eccnCode) {
            errors.eccnCode = true;
        }
        return errors;
    };

    useEffect(() => {
        const calculateTotalValue = () => {
            const total = values.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);

            // const totalQuantity = formData.items.reduce((sum, item) => {
            // const quantity = parseFloat(item.quantity) || 0;
            // return sum + quantity;
            // }, 0);

            setTotalItemsValue(total);

            setValues(prev => ({
                ...prev,
                cifValue: {
                    value: total.toFixed(2),
                    currency: prev.itemsCurrency // Use the currency from items
                }
            }));
            // setItemsTotalQty(totalQuantity);
        };

        calculateTotalValue();
    }, [values.items]);


    const handleSendToClientApproval = async () => {
        setAttemptedSubmit(true);
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            toast.error("Please fill all required fields");
            return;
        }

        try {
            setIsSendLoading(true);
            const quotesRef = collection(db, "quotes");
            const querySnapshot = await getDocs(quotesRef);
            const existingQuotes = querySnapshot.docs.map((doc) => doc.data().quoteNumber);

            let quoteNumber = `Q${new Date().getFullYear()}${String(estimateId).padStart(4, "0")}`;
            const settingsDocRef = doc(db, "settings", "settings");
            const settingsDocSnap = await getDoc(settingsDocRef);

            if (existingQuotes.includes(quoteNumber)) {
                const basePart = quoteNumber.slice(0, -4);
                const currentNumber = parseInt(quoteNumber.slice(-4));
                let nextNumber = currentNumber;
                do {
                    nextNumber++;
                    quoteNumber = `${basePart}${nextNumber.toString().padStart(4, "0")}`;
                } while (existingQuotes.includes(quoteNumber));
            }

            const totalWeight = values.dimensions.reduce(
                (acc, dim) => acc + (parseFloat(dim.weight.value) || 0) * (parseFloat(dim.quantity.value) || 1),
                0
            );

            // Calculate total item value correctly
            const calculatedTotalItemsValue = values.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);

            // Calculate CIF value as totalItemsValue * 1.10
            const calculatedCifValue = calculatedTotalItemsValue * 1.10;

            const firebaseData = {
                address: values.loadAddress || "",
                agreeTerms: true,
                assignAdmin: "",
                cargoValue: {
                    currency: values.itemsCurrency,
                    value: calculatedTotalItemsValue.toString()
                },
                cifValue: {
                    value: calculatedCifValue.toFixed(2),
                    currency: values.itemsCurrency
                },
                city: values.loadCity || "",
                comments: [],
                companyName: values.loadCompanyName || "",
                country: values.loadCountry || "",
                creationDate: new Date().toISOString(),
                currency: values.currency,
                description: "Estimate Sent for Client Approval",
                docFiles: [],
                email: values.email,
                eorGuarantee: "",
                inquireNumber: `IN${new Date().getFullYear()}-${estimateId.padStart(3, '0')}`,
                insurance: values.insurance || "0",
                insureShipment: values.insuranceSelected === "YES" ? "YES" : "",
                invoiceDetail: {
                    additionDetails: "",
                    invoiceFile: null,
                    isInsuranceChecked: values.insuranceSelected === "YES"
                },
                items: values.items.map(item => ({
                    countryOrigin: item.countryOrigin,
                    hasHSCode: !!item.itemHSCode,
                    itemHSCode: item.itemHSCode || "",
                    itemName: item.itemName,
                    itemValue: item.itemValue.toString(),
                    quantity: item.quantity.toString()
                })),
                loadAddress: values.loadAddress,
                loadAddress2: "",
                loadAddress_CompanyName: values.loadCompanyName,
                loadAddress_PhoneNumber: values.loadMobileNumber,
                loadAddress_PickUpDate: "",
                loadAddress_PickUpTime: {
                    startTime: "",
                    endTime: ""
                },
                loadAddress_ReferenceCustomer: values.loadReferenceCustomer,
                loadCity: values.loadCity,
                loadCountry: values.loadCountry,
                loadName: values.loadName,
                loadPostalCode: values.loadZipCode,
                modeOfTransfer: "By Air",
                name: values.userName,
                orderDetails: {
                    eta: "",
                    orderDate: "",
                    subStatus: ""
                },
                pcsPackage: values.dimensions.map(dim => ({
                    height: dim.height,
                    length: dim.length,
                    quantity: dim.quantity.value,
                    weight: dim.weight,
                    width: dim.width
                })),
                pkgType: values.pkgType,
                postalCode: values.postalCode,
                quoteCreatedDate: new Date().toISOString(),
                quoteNumber: quoteNumber,
                requestId: estimateId,
                requestStatus: "quoted",
                requiredDoc: values.requiredDoc,
                selectedTemplate: values.selectedTemplate,
                sendStatus: true,
                serviceType: values.selectedService,
                services: values.services,
                status: "pending",
                subTotal: values.subTotal,
                total: values.total,
                discount: values.discount,
                totalWeight: totalWeight,
                trackingNumber: "",
                unloadAddress: values.unloadAddress,
                unloadAddress2: "",
                unloadAddress_CompanyName: values.unloadCompanyName,
                unloadAddress_DeliveryDate: "",
                unloadAddress_DeliveryTime: {
                    startTime: "",
                    endTime: ""
                },
                unloadAddress_PhoneNumber: values.unloadMobileNumber,
                unloadAddress_ReferenceCustomer: values.unloadReferenceCustomer,
                unloadCity: values.unloadCity,
                unloadCountry: values.unloadCountry,
                unloadName: values.unloadName,
                unloadPostalCode: values.unloadZipCode,
                userCountry: values.userCountry,
                userId: values.userId,
                validityDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                vatAmount: values.vatAmount,
                vatPercentage: values.vatPercentage === "" ? "0" : values.vatPercentage,
                isInsuranceChecked: values.isInsuranceChecked,
            };

            // Save to Firebase
            const usersCollection = collection(db, "quotes");
            await addDoc(usersCollection, firebaseData);

            const basePart = quoteNumber.slice(0, -4);
            const currentNumber = parseInt(quoteNumber.slice(-4));
            const newQuoteNumber = `${basePart}${(currentNumber + 1).toString().padStart(4, "0")}`;

            await setDoc(
                settingsDocRef,
                {
                    quoteNumber_Prefix: newQuoteNumber
                },
                { merge: true }
            );

            await api.post(`${API_BASE_URL}/notifications`, {
                title: "New Quote Received",
                message: "Your proposal has been updated by the admin. Please check the updated proposal",
                linkText: "View Quote",
                link: "/newquotes",
                role: "user",
                userId: values.userId,
                quoteNumber: quoteNumber,
                type: "new-quote",
            });

            // Update approvalStatus in MongoDB
            await api.patch(`${API_BASE_URL}/clientestimates/approvalStatus/${estimateId}`, {
                approvalStatus: "Sent for Client Approval"
            });

            toast.success("Estimate sent to client for approval successfully!");
            setInitialValues(JSON.parse(JSON.stringify(values)));
            setHasChanges(false);
            navigate("/newRequest");
        } catch (error) {
            console.error("Error sending estimate for approval:", error);
            toast.error("Error sending estimate. Please try again later.");
        } finally {
            setIsSendLoading(false);
        }
    };

    const handleApprove = async () => {
        setAttemptedSubmit(true);
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            toast.error("Please fill all required fields");
            return;
        }

        if (hasChanges) {
            toast.error("Cannot approve with unsaved changes. Please update first.");
            return;
        }

        try {
            setIsApproveLoading(true);

            const totalWeight = values.dimensions.reduce(
                (acc, dim) => acc + (parseFloat(dim.weight.value) || 0) * (parseFloat(dim.quantity.value) || 1),
                0
            );

            // Calculate total item value correctly
            const calculatedTotalItemsValue = values.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);

            // Calculate CIF value as totalItemsValue * 1.10
            const calculatedCifValue = calculatedTotalItemsValue * 1.10;

            const firebaseData = {
                address: values.loadAddress || "",
                agreeTerms: true,
                assignAdmin: "",
                cargoValue: {
                    currency: values.itemsCurrency,
                    value: calculatedTotalItemsValue.toString()
                },
                cifValue: {
                    currency: values.itemsCurrency,
                    value: calculatedCifValue.toFixed(2)
                },
                city: values.loadCity || "",
                comments: [],
                companyName: values.loadCompanyName || "",
                country: values.loadCountry || "",
                creationDate: new Date().toISOString(),
                currency: values.currency,
                description: "Estimate Approval",
                docFiles: [],
                email: values.email,
                eorGuarantee: "",
                inquireNumber: `IN${new Date().getFullYear()}-${estimateId.padStart(3, '0')}`,
                insurance: "0",
                insureShipment: values.insuranceSelected === "YES" ? "YES" : "",
                invoiceDetail: {
                    additionDetails: "",
                    invoiceFile: null,
                    isInsuranceChecked: values.insuranceSelected === "YES"
                },
                items: values.items.map(item => ({
                    countryOrigin: item.countryOrigin,
                    hasHSCode: !!item.itemHSCode,
                    itemHSCode: item.itemHSCode || "",
                    itemName: item.itemName,
                    itemValue: item.itemValue.toString(),
                    quantity: item.quantity.toString()
                })),
                loadAddress: values.loadAddress,
                loadAddress2: "",
                loadAddress_CompanyName: values.loadCompanyName,
                loadAddress_PhoneNumber: values.loadMobileNumber,
                loadAddress_PickUpDate: "",
                loadAddress_PickUpTime: {
                    startTime: "",
                    endTime: ""
                },
                loadAddress_ReferenceCustomer: values.loadReferenceCustomer,
                loadCity: values.loadCity,
                loadCountry: values.loadCountry,
                loadName: values.loadName,
                loadPostalCode: values.loadZipCode,
                modeOfTransfer: "By Air",
                name: values.userName,
                orderDetails: {
                    eta: "",
                    orderDate: "",
                    subStatus: ""
                },
                pcsPackage: values.dimensions.map(dim => ({
                    height: dim.height,
                    length: dim.length,
                    quantity: dim.quantity.value,
                    weight: dim.weight,
                    width: dim.width
                })),
                pkgType: values.pkgType,
                postalCode: values.postalCode,
                quoteCreatedDate: new Date().toISOString(),
                quoteNumber: "",
                requestId: estimateId,
                requestStatus: "quoted",
                requiredDoc: values.requiredDoc,
                selectedTemplate: values.selectedTemplate,
                sendStatus: true,
                serviceType: values.selectedService,
                services: values.services,
                status: "in progress",
                subTotal: values.subTotal,
                total: values.total,
                discount: values.discount,
                totalWeight: totalWeight,
                trackingNumber: "",
                unloadAddress: values.unloadAddress,
                unloadAddress2: "",
                unloadAddress_CompanyName: values.unloadCompanyName,
                unloadAddress_DeliveryDate: "",
                unloadAddress_DeliveryTime: {
                    startTime: "",
                    endTime: ""
                },
                unloadAddress_PhoneNumber: values.unloadMobileNumber,
                unloadAddress_ReferenceCustomer: values.unloadReferenceCustomer,
                unloadCity: values.unloadCity,
                unloadCountry: values.unloadCountry,
                unloadName: values.unloadName,
                unloadPostalCode: values.unloadZipCode,
                userCountry: values.userCountry,
                userId: values.userId,
                validityDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                vatAmount: values.vatAmount,
                vatPercentage: values.vatPercentage === "" ? "0" : values.vatPercentage,
                insurance: values.insurance,
                isInsuranceChecked: values.isInsuranceChecked,
            };

            const quotesRef = collection(db, "quotes");
            const querySnapshot = await getDocs(quotesRef);
            const existingQuotes = querySnapshot.docs.map((doc) => doc.data().quoteNumber);
            const settingsDocRef = doc(db, "settings", "settings");
            const settingsDocSnap = await getDoc(settingsDocRef);
            let quoteNumber;

            if (settingsDocSnap.exists()) {
                const settingsData = settingsDocSnap.data();
                quoteNumber = settingsData.quoteNumber_Prefix || `Q${new Date().getFullYear()}${String(estimateId).padStart(4, "0")}`;

                if (existingQuotes.includes(quoteNumber)) {
                    const basePart = quoteNumber.slice(0, -4);
                    const currentNumber = parseInt(quoteNumber.slice(-4));
                    let nextNumber = currentNumber;
                    do {
                        nextNumber++;
                        quoteNumber = `${basePart}${nextNumber.toString().padStart(4, "0")}`;
                    } while (existingQuotes.includes(quoteNumber));
                }

                await updateDoc(settingsDocRef, {
                    quoteNumber_Prefix: `${quoteNumber.slice(0, -4)}${(parseInt(quoteNumber.slice(-4)) + 1).toString().padStart(4, "0")}`,
                });
            } else {
                quoteNumber = `Q${new Date().getFullYear()}${String(estimateId).padStart(4, "0")}`;
                await setDoc(settingsDocRef, {
                    quoteNumber_Prefix: quoteNumber
                });
            }

            firebaseData.quoteNumber = quoteNumber;
            firebaseData.trackingNumber = generateTrackingNumber();
            firebaseData.shipmentCreatedDate = new Date().toISOString();

            await api.post("/firestore/quotes/create-with-order", firebaseData);

            const itemNames = values.items.map((item) => item.itemName);
            const formattedItemNames = itemNames.join(", ");

            await api.post(`${API_BASE_URL}/notifications`, {
                title: "Proposal is Approved by Admin",
                message: `Your proposal has been approved successfully for ${formattedItemNames}. You can view and track your shipments from the 'My Shipments' tab.`,
                linkText: "View Quote",
                link: "/myShipment",
                // link: `/estimate/${estimateId}`,
                role: "user",
                userId: values.userId,
                type: "estimate-generated",
            });

            await api.post(`${API_BASE_URL}/mail/ior-notification-client`, {
                estimateData: values,
                clientEmail: values.email,
                link: `${API_DEPLOYED_URL}/estimate/${estimateId}`,
            });

            // Update approvalStatus in MongoDB
            await api.patch(`${API_BASE_URL}/clientestimates/approvalStatus/${estimateId}`, {
                approvalStatus: "Approved"
            });

            toast.success("Estimate approved and saved to Firebase successfully");
            navigate("/newRequest");
        } catch (error) {
            console.error("Error approving estimate:", error);
            toast.error("Failed to approve estimate");
        } finally {
            setIsApproveLoading(false);
        }
    };

    const renderEccnOption = (eccn) => (
        <div className="flex flex-col py-2 px-3 hover:bg-blue-50 transition-colors">
            <div className="flex justify-between items-center">
                <span className="font-semibold text-blue-600">{eccn.eccn_code}</span>
                <span
                    className={`text-xs px-2 py-1 rounded-full ${eccn.restrictions ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                >
                    {eccn.restrictions ? "Restricted" : "Unrestricted"}
                </span>
            </div>
            <span className="text-sm text-gray-600 mt-1">{eccn.description}</span>
        </div>
    );

    const handleDownloadPdf = async () => {
        setDownloadLoading(true);
        try {
            const estimate = { ...values, estimateId };
            const clientEstimateSummary = await fetchClientEstimateSummary(estimateId);
            if (!clientEstimateSummary) {
                throw new Error("Failed to fetch estimate summary");
            }

            const dhlEstimation = {
                feeTotals: clientEstimateSummary.dhlTaxes?.map(tax => ({
                    name: tax.name,
                    value: tax.value
                })) || []
            };

            if (!clientEstimateSummary.services.length && !dhlEstimation.feeTotals.length) {
                toast.warn("Estimate data is incomplete. Unable to download PDF.");
                setDownloadLoading(false);
                return;
            }

            const pdfBlob = await generateIOR(estimate, clientEstimateSummary, dhlEstimation);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = pdfUrl;
            link.download = `estimate_${estimate.estimateId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pdfUrl);
        } catch (error) {
            console.error("Error downloading PDF:", error);
            toast.error(`Error downloading PDF: ${error.message}`);
        } finally {
            setDownloadLoading(false);
        }
    };

    const onBack = () => navigate("/newRequest");

    const renderHintAlert = () => {
        if (values.approvalStatus === "Sent for Client Approval") {
            return (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md" role="alert">
                    <p className="font-bold text-lg">Quotation Alert</p>
                    <p className="text-sm">
                        Already Quotation created and sent to client approval. If you again click on Send Estimation to Client Approval then Another one Quotation will be created and will be sent to client approval. And If you click on Approve Estimation then Shipment will be created directly but duplicate shipment will occur.
                    </p>
                </div>
            );
        } else if (values.approvalStatus === "Approved") {
            return (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md" role="alert">
                    <p className="font-bold text-lg">Shipments Alert</p>
                    <p className="text-sm">
                        Already Shipment created. If you again click on Send Estimation to Client Approval then Another one Quotation will be created and will be sent to client approval. And If you click on Approve Estimation then Duplicate another one Shipment will be created directly but duplicate shipment will occur.
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <DashboardLayout>
            <div className="w-full">
                <Header title="Complete Estimation" />
                <div className="mb-3 p-4">
                    <button onClick={onBack} className="flex rounded-[5px] items-center space-x-4 py-2 px-4 text-white btnBg">
                        <FaArrowLeft className="mr-2" /> Back
                    </button>
                </div>

                <div className="p-4 bg-cus-gray">
                    <form className="p-4 mx-auto bg-white border rounded-md">
                        {values.approvalStatus && values.approvalStatus !== "Draft" && (
                            <div className="mx-[30px] mt-4">
                                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md" role="alert">
                                    <p className="font-bold text-lg">Proposal Status</p>
                                    <p className="text-sm">{values.approvalStatus}</p>
                                </div>
                            </div>
                        )}
                        <div className="mx-[30px] mt-4">
                            <h2 className="text-xl font-bold mb-4">Collection Address</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="w-full text-sm font-normal">Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${validationErrors.loadName ? "border-red-500" : ""}`}
                                        name="loadName"
                                        value={values.loadName}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Company Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadCompanyName"
                                        value={values.loadCompanyName}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Reference Customer <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadReferenceCustomer"
                                        value={values.loadReferenceCustomer}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Address <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadAddress"
                                        value={values.loadAddress}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Zip Code <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadZipCode"
                                        value={values.loadZipCode}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">City <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadCity"
                                        value={values.loadCity}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Country <span className="text-red-500">*</span></label>
                                    <CountrySelectInput
                                        name="loadCountry"
                                        value={values.loadCountry}
                                        onChange={handleChange}
                                        error={validationErrors.loadCountry}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Mobile Number</label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="loadMobileNumber"
                                        value={values.loadMobileNumber}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mx-[30px] mt-8">
                            <h2 className="text-xl font-bold mb-4">Delivery Address</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="w-full text-sm font-normal">Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${validationErrors.unloadName ? "border-red-500" : ""}`}
                                        name="unloadName"
                                        value={values.unloadName}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Company Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadCompanyName"
                                        value={values.unloadCompanyName}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Reference Customer <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadReferenceCustomer"
                                        value={values.unloadReferenceCustomer}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Address <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadAddress"
                                        value={values.unloadAddress}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Zip Code <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadZipCode"
                                        value={values.unloadZipCode}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">City <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadCity"
                                        value={values.unloadCity}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Country <span className="text-red-500">*</span></label>
                                    <CountrySelectInput
                                        name="unloadCountry"
                                        value={values.unloadCountry}
                                        onChange={handleChange}
                                        error={validationErrors.unloadCountry}
                                    />
                                </div>
                                <div>
                                    <label className="w-full text-sm font-normal">Mobile Number</label>
                                    <input
                                        type="text"
                                        className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                        name="unloadMobileNumber"
                                        value={values.unloadMobileNumber}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 mt-8" style={{ marginLeft: "27px", width: "1191px" }}>
                            {values.dimensions.map((dimension, index) => (
                                <div key={index} className="relative border p-4 rounded-md">
                                    <div className="grid grid-cols-5 gap-4">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-normal">Weight <span className="text-red-500">*</span></label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    className={`w-28 p-1 text-sm border rounded-md outline-none ${validationErrors[`weight-${index}`] ? "border-red-500" : ""}`}
                                                    onChange={(e) => handleDimensionChange(index, "weight", e)}
                                                    value={dimension.weight.value}
                                                />
                                                <Select
                                                    style={{ width: 80 }}
                                                    value={dimension.weight.unit}
                                                    onChange={(unit) => handleDimensionUnitChange(index, "weight", unit)}
                                                >
                                                    <Option value="kg">kg</Option>
                                                    <Option value="lb">lb</Option>
                                                </Select>
                                            </div>
                                        </div>
                                        {["length", "width", "height"].map((dim) => (
                                            <div key={dim} className="flex flex-col gap-1">
                                                <label className="text-sm font-normal">{dim.charAt(0).toUpperCase() + dim.slice(1)} <span className="text-red-500">*</span></label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        className={`w-28 p-1 text-sm border rounded-md outline-none ${validationErrors[`${dim}-${index}`] ? "border-red-500" : ""}`}
                                                        onChange={(e) => handleDimensionChange(index, dim, e)}
                                                        value={dimension[dim].value}
                                                    />
                                                    {dim === "height" && (
                                                        <Select
                                                            style={{ width: 80 }}
                                                            value={dimension[dim].unit || "cm"}
                                                            onChange={(unit) => handleDimensionUnitChange(index, dim, unit)}
                                                        >
                                                            <Option value="cm">cm</Option>
                                                            <Option value="in">in</Option>
                                                        </Select>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-normal">Quantity <span className="text-red-500">*</span></label>
                                            <div className="flex items-center">
                                                <input
                                                    type="number"
                                                    className={`w-28 p-1 text-sm border rounded-md outline-none ${validationErrors[`quantity-${index}`] ? "border-red-500" : ""}`}
                                                    onChange={(e) => handleDimensionChange(index, "quantity", e)}
                                                    value={dimension.quantity.value}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute right-2 top-2 flex space-x-2">
                                        {index === values.dimensions.length - 1 && (
                                            <button type="button" onClick={handleAddDimension} className="text-green-500">
                                                <FaCirclePlus />
                                            </button>
                                        )}
                                        {values.dimensions.length > 1 && (
                                            <button type="button" onClick={() => handleRemoveDimension(index)} className="text-red-500">
                                                <FaCircleMinus />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mx-[30px] flex flex-wrap md:flex-nowrap gap-3 mt-4">
                            <div className="w-full md:w-1/2">
                                <label className="text-sm font-normal">Currency <span className="text-red-500">*</span></label>
                                <Select
                                    style={{ width: "100%" }}
                                    value={values.airfreightCurrency}
                                    onChange={(value) => handleSelectChange("airfreightCurrency", value)}
                                >
                                    <Option value="USD">USD</Option>
                                    <Option value="EUR">EUR</Option>
                                    <Option value="GBP">GBP</Option>
                                </Select>
                            </div>
                            <div className="w-full md:w-1/2">
                                <label className="text-sm font-normal">Package Type <span className="text-red-500">*</span></label>
                                <Select
                                    style={{ width: "100%" }}
                                    value={values.pkgType}
                                    onChange={(value) => handleSelectChange("pkgType", value)}
                                >
                                    <Option value="pallet">Pallet</Option>
                                    <Option value="box">Box</Option>
                                    <Option value="container">Container</Option>
                                </Select>
                            </div>
                        </div>

                        <div className="mx-[30px] flex flex-wrap md:flex-nowrap gap-3 mt-4">
                            <div className="w-full md:w-1/2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Insurance Selected by Client <span className="text-red-500">*</span>
                                </label>
                                <Radio.Group onChange={handleInsuranceChange} value={values.insuranceSelected}>
                                    <Radio value="YES">Yes</Radio>
                                    <Radio value="NO">No</Radio>
                                </Radio.Group>
                            </div>
                            <div className="w-full md:w-1/2">
                                {/* Show Document Required only for IOR */}
                                {values.selectedService === "IOR" && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Import documentation required? <span className="text-red-500">*</span>
                                        </label>
                                        <Radio.Group onChange={handleRequiredDocChange} value={values.requiredDoc}>
                                            <Radio value="Required">Required</Radio>
                                            <Radio value="not_Required">Not Required</Radio>
                                        </Radio.Group>
                                    </div>
                                )}
                                {/* Show ECCN Code only for EOR */}
                                {values.selectedService === "EOR" && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            ECCN Code <span className="text-red-500">*</span>
                                        </label>
                                        <Select
                                            showSearch
                                            placeholder="Select ECCN Code"
                                            value={values.eccnCode}
                                            onChange={(value) => setSelectedEccn(value)}
                                            className={validationErrors.eccnCode ? "border-red-500" : ""}
                                            style={{ width: "100%" }}
                                            optionLabelProp="label"
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                option.children.props.children.some((child) =>
                                                    child.toString().toLowerCase().includes(input.toLowerCase())
                                                )
                                            }
                                            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                                        >
                                            {eccnCodes.map((eccn) => (
                                                <Select.Option
                                                    key={eccn.eccn_code}
                                                    value={eccn.eccn_code}
                                                    label={eccn.eccn_code}
                                                >
                                                    {renderEccnOption(eccn)}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                        {validationErrors.eccnCode && (
                                            <p className="text-red-500 text-xs mt-1">
                                                ECCN Code is required for EOR service
                                            </p>
                                        )}
                                    </div>
                                )}
                                {/* Show both when service is neither purely EOR nor IOR (if applicable) */}
                                {values.selectedService !== "EOR" && values.selectedService !== "IOR" && (
                                    <>
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Import documentation required? <span className="text-red-500">*</span>
                                            </label>
                                            <Radio.Group onChange={handleRequiredDocChange} value={values.requiredDoc}>
                                                <Radio value="Required">Required</Radio>
                                                <Radio value="not_Required">Not Required</Radio>
                                            </Radio.Group>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                ECCN Code <span className="text-red-500">*</span>
                                            </label>
                                            <Select
                                                showSearch
                                                placeholder="Select ECCN Code"
                                                value={values.eccnCode}
                                                onChange={(value) => setSelectedEccn(value)}
                                                className={validationErrors.eccnCode ? "border-red-500" : ""}
                                                style={{ width: "100%" }}
                                                optionLabelProp="label"
                                                optionFilterProp="children"
                                                filterOption={(input, option) =>
                                                    option.children.props.children.some((child) =>
                                                        child.toString().toLowerCase().includes(input.toLowerCase())
                                                    )
                                                }
                                                dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                                            >
                                                {eccnCodes.map((eccn) => (
                                                    <Select.Option
                                                        key={eccn.eccn_code}
                                                        value={eccn.eccn_code}
                                                        label={eccn.eccn_code}
                                                    >
                                                        {renderEccnOption(eccn)}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                            {validationErrors.eccnCode && (
                                                <p className="text-red-500 text-xs mt-1">
                                                    ECCN Code is required for EOR service
                                                </p>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="mx-[30px] mt-8">
                            <h2 className="text-xl font-bold mb-4">Item Details</h2>
                            {values.items.map((item, index) => (
                                <div key={index} className="mb-6 p-4 border rounded-lg relative">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium">Item Name <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${validationErrors[`itemName-${index}`] ? "border-red-500" : ""}`}
                                                value={item.itemName}
                                                onChange={(e) => handleItemChange(index, "itemName", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Quantity <span className="text-red-500">*</span></label>
                                            <input
                                                type="number"
                                                className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${validationErrors[`quantity-${index}`] ? "border-red-500" : ""}`}
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Item Value <span className="text-red-500">*</span></label>
                                            <input
                                                type="number"
                                                className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${validationErrors[`itemValue-${index}`] ? "border-red-500" : ""}`}
                                                value={item.itemValue}
                                                onChange={(e) => handleItemChange(index, "itemValue", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">Country of Origin <span className="text-red-500">*</span></label>
                                            <CountrySelectInput
                                                name={`countryOrigin-${index}`}
                                                value={item.countryOrigin}
                                                onChange={(e) => handleItemChange(index, "countryOrigin", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium">HS Code <span className="text-red-500">*</span></label>
                                            <input
                                                type="text"
                                                className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                                value={item.itemHSCode}
                                                onChange={(e) => handleItemChange(index, "itemHSCode", e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="absolute right-2 top-2 flex space-x-2">
                                        {index === values.items.length - 1 && (
                                            <button type="button" onClick={handleAddItem} className="text-green-500">
                                                <FaCirclePlus />
                                            </button>
                                        )}
                                        {values.items.length > 1 && (
                                            <button type="button" onClick={() => handleRemoveItem(index)} className="text-red-500">
                                                <FaCircleMinus />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mx-[30px] mt-8">
                            <ServiceTemp
                                formData={values}
                                setFormData={(formData) => {
                                    setValues(formData);
                                    updateHasChanges(formData);
                                }}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                templateSelected={templateSelected}
                                attemptedSubmit={attemptedSubmit}
                                setTemplateSelected={setTemplateSelected}
                            />
                        </div>

                        <div className="mx-[30px] flex flex-col lg:flex-row gap-2 mt-4">
                            <div className="w-full lg:w-[60%] bg-[#FBFCFF] p-4 rounded-lg border border-cus-blue">
                                <div className="flex items-center w-full lg:w-1/2 gap-1 py-2">
                                    <label className="w-full text-base font-medium">
                                        VAT Percentage:
                                    </label>
                                    <input
                                        type="number"
                                        className={`w-full p-2 text-sm border-2 rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${validationErrors.vatPercentage ? "border-red-500" : ""}`}
                                        name="vatPercentage"
                                        placeholder="%"
                                        id="vatPercentage"
                                        onWheel={(e) => {
                                            // Prevent mouse wheel from changing the value when focused
                                            e.currentTarget.blur();
                                        }}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            // Allow empty input (treated as 0), allow non-negative decimals only
                                            if (value === "") {
                                                handleChange({ target: { name: "vatPercentage", value: "" } });
                                                return;
                                            }
                                            // Block invalid characters early
                                            if (!/^\d*\.?\d*$/.test(value)) return;
                                            // Disallow leading zero prefix like 05, 0007 (but allow 0.x)
                                            if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
                                                value = value.replace(/^0+/, "");
                                            }
                                            if (value.startsWith(".")) {
                                                value = `0${value}`;
                                            }
                                            // Ensure non-negative number
                                            if (value !== "" && parseFloat(value) < 0) return;
                                            handleChange({ target: { name: "vatPercentage", value } });
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "e" || e.key === "-" || e.key === "+") {
                                                e.preventDefault();
                                            }
                                        }}
                                        value={values.vatPercentage || ""}
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                {/* <InsuranceCheckbox
                                    formData={values}
                                    setFormData={(newFormData) => {
                                        setValues(newFormData);
                                        updateHasChanges(newFormData);
                                    }}
                                /> */}
                            </div>

                            <div className="w-full lg:w-[40%] bg-[#FBFCFF] p-4 border rounded-lg border-cus-blue">
                                <div className="flex justify-between">
                                    <div>
                                        <p className="text-[14px]">Sub Total</p>
                                    </div>
                                    <div className="">
                                        <p className="text-[14px] font-extrabold">
                                            {AmountFormat(values.subTotal, values.currency)}
                                        </p>
                                    </div>
                                </div>

                                <DiscountInput
                                    discountType={discountType}
                                    discountValue={discountValue}
                                    onDiscountTypeChange={(type) => {
                                        setDiscountType(type);
                                        updateHasChanges({ ...values });
                                    }}
                                    onDiscountValueChange={(val) => {
                                        setDiscountValue(val);
                                        updateHasChanges({ ...values });
                                    }}
                                    currency={values.currency}
                                    discountAmount={values.discount?.amount || 0}
                                />

                                {values.discount && values.discount.amount > 0 && (
                                    <div className="flex justify-between mt-1 text-orange-600">
                                        <div>
                                            <p className="text-[14px] font-medium">
                                                Discount ({values.discount.type === 'percentage' ? `${Math.abs(values.discount.value)}%` : 'Fixed'})
                                            </p>
                                        </div>
                                        <div className="">
                                            <p className="text-[14px] font-extrabold">
                                                - {AmountFormat(values.discount.amount, values.currency)}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-between mt-1">
                                    <div>
                                        <p className="text-[14px]">Total VAT</p>
                                    </div>
                                    <div className="">
                                        <p className="text-[14px] font-extrabold">
                                            {AmountFormat(values.vatAmount, values.currency)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex justify-between mt-4 border-t border-cus-blue">
                                    <div className="mt-2">
                                        <p className="text-[14px]">Total</p>
                                    </div>
                                    <div className="mt-2">
                                        <p className="text-[14px] font-extrabold">
                                            {AmountFormat(values.total, values.currency)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {values.approvalStatus && values.approvalStatus !== "Draft" && (
                            <div className="mx-[30px] mt-4">
                                {renderHintAlert()}
                            </div>
                        )}

                        <div className="mx-[30px] flex gap-3 mt-4 justify-between">
                            <div className="flex gap-3">
                                <CustomButton label="Send Estimation to Client Approval" onClick={handleSendToClientApproval} loading={isSendLoading} />
                                <CustomButton label="Approve Estimation" onClick={handleApprove} loading={isApproveLoading} disabled={hasChanges || isApproveLoading} />
                            </div>
                            <PDFDownloadButton onClick={handleDownloadPdf} loading={downloadLoading} />
                        </div>
                    </form>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default CompleteEstimationForm;
